// useProjectCapabilities.js
//
// Загружает эффективные capabilities проектного пользователя:
//   GET /api/p/:slug/auth/capabilities
//   →  {
//        role, tabs, actions, commandPriority,            // ACL пользователя
//        plan, modules: { trends: bool, ... }, quotas: {...}, // product entitlements
//      }
//
// Поведение:
//   1. Если slug пустой → возвращаем role-based интерим без сетевого запроса.
//   2. Если backend ещё не имплементировал endpoint (404/501) — fallback на интерим.
//   3. На любую ошибку — интерим + warn в консоль (не блокируем UI).
//   4. Старый бэкенд без полей plan/modules/quotas просто отдаёт null'ы —
//      потребители (PR-B: <RequireModule>) трактуют это как «всё включено».
//
// Используется SuperAdmin shell и админскими табами для:
//   - getVisibleTabs (какие табы показать)
//   - capability props (readOnly, canCreate, canDelete)
//   - quotas (например maxStations) в будущем
//   - product modules (RequireModule guard, показ blur+CTA на superadmin)

import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import axios from 'axios';
import { resolveCapabilities } from '../pages/superadmin-shell/superAdminTabs';

const selectAuthRole = createSelector(
  [(state) => state.authSlice],
  (auth) => auth?.role ?? null,
);

function getAuthToken() {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  } catch (_e) {
    return '';
  }
}

export function useProjectCapabilities(projectSlug) {
  const role = useSelector(selectAuthRole);
  const [serverCapabilities, setServerCapabilities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedSlug, setLoadedSlug] = useState('');

  useEffect(() => {
    if (!projectSlug) {
      setServerCapabilities(null);
      setError(null);
      setLoading(false);
      setLoadedSlug('');
      return undefined;
    }
    let mounted = true;
    const token = getAuthToken();
    if (!token) {
      setServerCapabilities(null);
      setLoading(false);
      setLoadedSlug(projectSlug);
      return undefined;
    }
    setLoading(true);
    setLoadedSlug('');
    axios
      .get(`${window.location.origin}/api/p/${projectSlug}/auth/capabilities`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!mounted) return;
        const payload = res?.data || null;
        setServerCapabilities(payload && typeof payload === 'object' ? payload : null);
        setError(null);
      })
      .catch((e) => {
        if (!mounted) return;
        // 404/501 — endpoint ещё не задеплоен; fallback на интерим без шума.
        const status = e?.response?.status;
        if (status !== 404 && status !== 501) {
          console.warn('useProjectCapabilities: failed to load capabilities', status, e?.message);
        }
        setServerCapabilities(null);
        setError(e);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
        setLoadedSlug(projectSlug);
      });
    return () => {
      mounted = false;
    };
  }, [projectSlug]);

  const capabilities = useMemo(
    () => resolveCapabilities({ role, serverCapabilities }),
    [role, serverCapabilities],
  );

  const commandPriority = useMemo(() => {
    if (serverCapabilities?.commandPriority !== undefined) {
      const p = Number(serverCapabilities.commandPriority);
      if (Number.isFinite(p)) return Math.max(0, Math.min(9, p));
    }
    if (role === 'superadmin' || role === 'support') return 9;
    return null;
  }, [serverCapabilities, role]);

  // Product entitlements — приходят из catalog через ProjectFeaturesService.
  // На бэкенде до миграции 2026_05_24 эти поля отсутствуют — отдаём null,
  // потребители трактуют null как "нет данных, ничего не гасим".
  const modules = useMemo(() => {
    const m = serverCapabilities?.modules;
    return m && typeof m === 'object' ? m : null;
  }, [serverCapabilities]);

  const quotas = useMemo(() => {
    const q = serverCapabilities?.quotas;
    return q && typeof q === 'object' ? q : null;
  }, [serverCapabilities]);

  const plan = useMemo(() => {
    const p = serverCapabilities?.plan;
    return typeof p === 'string' && p.length > 0 ? p : null;
  }, [serverCapabilities]);

  const billingSuspended = useMemo(() => {
    const data = error?.response?.data;
    return data?.code === 'PROJECT_BILLING_SUSPENDED';
  }, [error]);

  const capabilitiesReady = !projectSlug || loadedSlug === projectSlug;

  return {
    capabilities,
    commandPriority,
    loading,
    capabilitiesReady,
    error,
    billingSuspended,
    role,
    modules,
    quotas,
    plan,
  };
}
